// React
import { FunctionComponent, useCallback, useEffect, useState } from "react";
// Navigation
import { useNavigate } from "react-router-dom";
// Components
import JunoPage from "../../components/JunoPage/JunoPage";
// Font awesome
import { faEye } from "@fortawesome/free-solid-svg-icons";
// Fhir front library
import { SearchableTable } from "@fyrstain/hl7-front-library";
// Translation
import i18n from "i18next";
// Resources
import { Organization } from "fhir/r5";
// Services
import {
  getOrganizationTypeLabel,
  hasOrganizationTypeValueSet,
  loadOrganizationTypeOptions,
  OrganizationTypeOption,
} from "../../services/OrganizationTypeService";
// Styles
import "./Organizations.css";
import { useErrorNavigation } from "../../services/useErrorNavigation";
import { Alert } from "react-bootstrap";

const Organizations: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();
  const [organizationTypeOptions, setOrganizationTypeOptions] = useState<
    OrganizationTypeOption[]
  >([]);
  const [isOrganizationTypeFilterUnavailable, setOrganizationTypeFilterUnavailable] =
    useState(false);

  const onDetails = useCallback(
    (id: string) => {
      navigate("/Organization/" + id);
    },
    [navigate]
  );

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useErrorNavigation();

  useEffect(() => {
    let active = true;

    loadOrganizationTypeOptions()
      .then((options) => {
        if (active) {
          setOrganizationTypeOptions(options);
          setOrganizationTypeFilterUnavailable(false);
        }
      })
      .catch(() => {
        if (active) {
          setOrganizationTypeOptions([]);
          setOrganizationTypeFilterUnavailable(hasOrganizationTypeValueSet);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage titleKey={i18n.t("title.organizationlist")} needsLogin={true}>
      <div className="organizations-table">
        {isOrganizationTypeFilterUnavailable && (
          <Alert variant="warning" role="status">
            {i18n.t("warning.filtersunavailable")}
          </Alert>
        )}
        <SearchableTable
          searchCriteriaProperties={{
            title: i18n.t("title.searchcriteria"),
            submitButtonLabel: i18n.t("button.search"),
            resetButtonLabel: i18n.t("button.reset"),
            language: i18n.t,
            fixedParameters: {
              _elements: "id,name,identifier,partOf,type",
              _sort: "name",
              _tag: "internal",
            },
            inputs: [
              {
                label: i18n.t("label.organizationname"),
                type: "text",
                searchParamsName: "name:contains",
              },
              {
                label: i18n.t("label.identifier"),
                type: "text",
                searchParamsName: "identifier",
              },
              {
                label: i18n.t("label.parentidentifier"),
                type: "text",
                searchParamsName: "partof.identifier",
              },
              {
                label: i18n.t("label.organizationtype"),
                type: "select",
                placeholder: i18n.t("label.organizationtype"),
                searchParamsName: "type",
                options: organizationTypeOptions,
              },
            ],
          }}
          paginatedTableProperties={{
            columns: [
              {
                header: i18n.t("label.organizationname"),
                dataField: "name",
                width: "40%",
              },
              {
                header: i18n.t("label.identifier"),
                dataField: "identifier",
                width: "25%",
              },
              {
                header: i18n.t("label.parentidentifier"),
                dataField: "parentIdentifier",
                width: "20%",
              },
              {
                header: i18n.t("label.organizationtype"),
                dataField: "organizationType",
                width: "15%",
              },
            ],
            action: [
              {
                icon: faEye,
                onClick: onDetails,
              },
            ],
            mapResourceToData: (resource: Organization) => {
              const primaryIdentifier =
                resource.identifier?.find(
                  (identifier) => identifier.use === "official"
                )?.value ??
                resource.identifier?.[0]?.value ??
                resource.id ??
                "N/A";

              const parentIdentifier =
                resource.partOf?.identifier?.value ??
                resource.partOf?.reference?.split("/").pop() ??
                "N/A";

              return {
                id: resource.id ?? "N/A",
                name: resource.name ?? "N/A",
                identifier: primaryIdentifier,
                parentIdentifier,
                organizationType: getOrganizationTypeLabel(
                  resource,
                  organizationTypeOptions
                ),
              };
            },
            searchProperties: {
              serverUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
              resourceType: "Organization",
            },
            onError: onError,
          }}
        />
      </div>
    </JunoPage>
  );
};

export default Organizations;
